import React, { useState, useContext, useEffect } from "react";
import Header from "../components/HeaderB";
import ProfileSidebar from "../components/ProfileSideBar";
import { AuthContext } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Upload, Camera, Plus, PlusCircle } from "lucide-react";
import { useRef } from "react";
import { translateText } from "../utils/categoryTranslations";
import { queuePendingAction, getFormData } from '../utils/offlineDB';

//reusable component for the Form fields
const FieldRow = ({ label, children }) => (
  <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 w-full overflow-hidden">
    <span className="w-full md:w-32 font-semibold text-custom-letters text-left shrink-0">
      {label}:
    </span>
    <div className="w-full flex-1 text-left">
      {children}
    </div>
  </div>
);

//main component
const AddDiscount = () => {
  const { user, accessToken, language } = useContext(AuthContext);

  const translations = {
    el: {
      title: "Προσθήκη νέας προσφοράς",
      locationHeader: "Εισάγετε τοποθεσία προσφοράς",
      detailsHeader: "Εισάγετε λεπτομέρειες προσφοράς",
      city: "Πόλη",
      area: "Περιοχή",
      store: "Κατάστημα",
      product: "Προϊόν",
      category: "Κατηγορία",
      originalPrice: "Αρχική τιμή",
      discountPrice: "Τιμή προσφοράς",
      discount: "Έκπτωση (%)",
      selectCity: "Επιλέξτε πόλη",
      selectArea: "Επιλέξτε περιοχή",
      selectCityFirst: "Επιλέξτε πρώτα πόλη",
      selectLocation: "Επιλέξτε τοποθεσία",
      selectCategory: "Επιλέξτε κατηγορία",
      addNewStore: "Προσθήκη νέου καταστήματος",
      addNewCat: "Προσθήκη νέας κατηγορίας",
      upload: "Νέα φωτογραφία",
      camera: "Κάμερα",
      publish: "Δημοσίευση",
      successTitle: "Η προσφορά προστέθηκε",
      successMsg: "Η προσφορά σας αποθηκεύτηκε με επιτυχία.",
      formIncomplete: "Παρακαλώ συμπληρώστε όλα τα απαιτούμενα πεδία.",
      priceError: "Η τιμή προσφοράς πρέπει να είναι χαμηλότερη από την αρχική.",
      errorGeneric: "Κάτι πήγε στραβά",
      errorServer: "Σφάλμα διακομιστή",
      cancel: "Ακύρωση",
      save: "Αποθήκευση",
      errorTitle: "Σφάλμα",
      validationTitle: "Παρακαλώ ελέγξτε",
      ok: "Εντάξει",
      offlineTitle: "Αποθηκεύτηκε για αργότερα",
      offlineMsg: "Είστε εκτός σύνδεσης. Η προσφορά σας θα δημοσιευτεί αυτόματα όταν επανασυνδεθείτε."
    },
    en: {
      title: "Add new offer",
      locationHeader: "Enter offer location",
      detailsHeader: "Enter offer details",
      city: "City",
      area: "Area",
      store: "Store",
      product: "Product",
      category: "Category",
      originalPrice: "Original price",
      discountPrice: "Offer price",
      discount: "Discount (%)",
      selectCity: "Select city",
      selectArea: "Select area",
      selectCityFirst: "Select city first",
      selectLocation: "Select location",
      selectCategory: "Select category",
      addNewStore: "Add new store",
      addNewCat: "Add new category",
      upload: "Upload",
      camera: "Camera",
      publish: "Publish",
      successTitle: "Offer added",
      successMsg: "Your offer was successfully saved.",
      formIncomplete: "Please complete all required fields.",
      priceError: "Offer price must be lower than original price",
      errorGeneric: "Something went wrong",
      errorServer: "Server error",
      cancel: "Cancel",
      save: "Save",
      errorTitle: "Error",
      validationTitle: "Please check",
      ok: "OK",
      offlineTitle: "Saved for later",
      offlineMsg: "You are offline. Your offer will be published automatically when you reconnect."
    }
  };

  const t = translations[language] || translations.el;

  const navigate = useNavigate(); //redirect

  //form state
  const [form, setForm] = useState({
    title: "",
    category: "",
    originalPrice: "",
    discountPrice: "",
    discount: "",
    city: "",
    area: "",
    store: "",
  });

  //for the popup
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [offlineQueuedPopup, setOfflineQueuedPopup] = useState(false);
  const [errorPopup, setErrorPopup] = useState(null);

  //the photo file send to the back
  const [photoFile, setPhotoFile] = useState(null);
  //for viewing the photo on the screen 
  const [photoPreview, setPhotoPreview] = useState(null);

  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [areas, setAreas] = useState([]);
  const [locations, setLocations] = useState([]);

  const cameraInputRef = useRef(null);

  const [showCategoryPopup, setShowCategoryPopup] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");

  //for updating the form state
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  //photo handling
  const handlePhoto = (file) => {
    setPhotoFile(file); //photo file for back
    setPhotoPreview(URL.createObjectURL(file)); //temp URL for preview
  };

  const handleSubmit = async () => {
    //check if user exists
    if (!user || !accessToken) return;

    //if a form value is missing warning message
    if (!isFormValid) {
      setErrorPopup({ title: t.validationTitle, msg: t.formIncomplete });
      return;
    }

    // check if Discount Price < Original Price
    if (+form.discountPrice >= +form.originalPrice) {
      setErrorPopup({ title: t.validationTitle, msg: t.priceError });
      return;
    }

    const fromlanguage = language; 
    const tolanguage = language === "el" ? "en" : "el";

    // Call translation API to translate the discount title to the other language
    const translatedTitle = await translateText(form.title, fromlanguage, tolanguage);

    const titleEn = language === "en" ? form.title : translatedTitle;
    const titleEl = language === "el" ? form.title : translatedTitle;

    // If offline, queue the discount for replay when the user reconnects
    if (!navigator.onLine) {
      await queuePendingAction({
        type: 'ADD_DISCOUNT',
        url: `${process.env.REACT_APP_BACKEND_URL}/adddiscount/add`,
        method: 'POST',
        fields: {
          titleEn,
          titleEl,
          category: form.category,
          originalPrice: form.originalPrice,
          discountPrice: form.discountPrice,
          discount: form.discount,
          store: form.store,
        },
        photoBlob: photoFile || null,
        photoName: photoFile?.name || null,
        photoType: photoFile?.type || null,
        userId: user?.user_id,
      });
      setOfflineQueuedPopup(true);
      return;
    }

    const payload = new FormData();
    // Add each field into the payload with both language versions
    // Send both English and Greek versions of the title to the backend
    payload.append("titleEn", titleEn);
    payload.append("titleEl", titleEl);
    payload.append("category", form.category);
    payload.append("originalPrice", form.originalPrice);
    payload.append("discountPrice", form.discountPrice);
    payload.append("discount", form.discount);
    //photos are not required for save
    if (photoFile) {
      payload.append("photo", photoFile);
    }
    payload.append("store", form.store);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/adddiscount/add`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: payload, //formData
        }
      );

      //if everything goes okey show popup
      if (res.ok) {
        setShowSuccessPopup(true);
      } else {
        setErrorPopup({ title: t.errorTitle, msg: t.errorGeneric });
      }
    } catch (err) {
      console.error(err);
      setErrorPopup({ title: t.errorTitle, msg: t.errorServer });
    }
  };

  useEffect(() => {
    //fetch categories
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/adddiscount/categories?lang=${language}`,
          {
            headers: { Authorization: `Bearer ${accessToken}` }
          });
        const json = await res.json();
        setCategories(json.categories);
      } catch (err) {
        // Offline fallback: read from IndexedDB
        const cached = await getFormData(`dropdown_${language || 'el'}`);
        if (cached?.categories) setCategories(cached.categories);
      }
    };
    fetchCategories();
  },
    [language, accessToken]);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/adddiscount/cities?lang=${language}`,
          {
            headers: { Authorization: `Bearer ${accessToken}` }
          }
        );
        const json = await res.json();
        setCities(json.cities);
      } catch {
        // Offline fallback: derive unique cities from the cached stores list
        const cached = await getFormData(`dropdown_${language || 'el'}`);
        if (cached?.stores) {
          const cities = [...new Set(cached.stores.map(s => s.city).filter(Boolean))];
          setCities(cities);
        }
      }
    };
    fetchCities();
  }, []);

  //depends on the city dropdown
  useEffect(() => {
    //if a city is not selected no calling API
    if (!form.city) return;

    const fetchAreas = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/adddiscount/areas?city=${form.city}&lang=${language}`,
          {
            headers: { Authorization: `Bearer ${accessToken}` }
          }
        );
        const json = await res.json();
        setAreas(json.areas);
      } catch {
        // Offline fallback: derive areas for this city from cached stores
        const cached = await getFormData(`dropdown_${language || 'el'}`);
        if (cached?.stores) {
          const areas = [...new Set(
            cached.stores.filter(s => s.city === form.city).map(s => s.area).filter(Boolean)
          )];
          setAreas(areas);
        }
      }
    };

    fetchAreas();
  }, [form.city]);

  //depends on the city and area dropdowns
  useEffect(() => {
    //if a city and an area is not selected no calling API
    if (!form.city || !form.area) return;

    const fetchStores = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/adddiscount/stores?city=${form.city}&areas=${form.area}&lang=${language}`,
          {
            headers: { Authorization: `Bearer ${accessToken}` }
          }
        );
        const json = await res.json();
        setLocations(json.stores);
      } catch {
        // Offline fallback: filter cached stores by city and area
        const cached = await getFormData(`dropdown_${language || 'el'}`);
        if (cached?.stores) {
          const stores = cached.stores.filter(
            s => s.city === form.city && s.area === form.area
          );
          setLocations(stores);
        }
      }
    };
    fetchStores();
  }, [form.city, form.area]);

  // Automatic discount percentage calculation when prices change
  useEffect(() => {
    const original = parseFloat(form.originalPrice);
    const discounted = parseFloat(form.discountPrice);

    if (original > 0 && discounted >= 0 && discounted < original) {
      const calcDiscount = ((original - discounted) / original) * 100;
      // Update state with the calculated discount rounded to 2 decimal places
      setForm(prev => ({ ...prev, discount: calcDiscount.toFixed(2) }));
    } else {
      // If prices are invalid, clear the discount field
      setForm(prev => ({ ...prev, discount: "" }));
    }
  }, [form.originalPrice, form.discountPrice]);

  const handleSaveCategory = async () => {
    if (!newCategoryName) return;

    // Determine the translation direction based on the current language preference
    const fromlanguage = language; // "en" or "el"
    const tolanguage = language === "en" ? "el" : "en";

    // Execute translation API call
    const translated = await translateText(newCategoryName, fromlanguage, tolanguage);

    // Prepare payload for database insertion
    // category_name always contains English, category_name_el always contains Greek
    const payload = {
      nameEn: language === "en" ? newCategoryName : translated,
      nameEl: language === "el" ? newCategoryName : translated
    };

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/adddiscount/categories/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        const json = await res.json();
        setCategories([...categories, json.category]);
        setForm({ ...form, category: json.category.category_id });
        setShowCategoryPopup(false);
        setNewCategoryName("");
      }
    } catch (err) {
      console.error(err);
    }
  };

  //boolean that checks if all mandatory fields are filled
  const isFormValid =
    form.title !== "" &&
    form.category !== "" &&
    form.originalPrice !== "" &&
    form.discountPrice !== "" &&
    form.city !== "" &&
    form.area !== "" &&
    form.store !== "";

  return (
    <div className="min-h-screen bg-custom-background flex flex-col">
      <Header />
      <div className="flex-1 flex flex-row relative bg-custom-background">
        <ProfileSidebar />

        <main id="adddiscount-content" className="flex flex-col flex-1 items-center justify-start px-4 md:px-10 py-10 pt-20 md:pt-10 xl:ml-20 text-center md:text-left">
          <div className="w-full max-w-3xl mx-auto bg-custom-header rounded-3xl shadow-xl p-8 md:p-12">
            <div className="flex flex-col gap-6">
              <h2 className="text-2xl font-semibold mb-8 text-custom-letters text-center flex justify-center items-center gap-2">
                <PlusCircle className="text-custom-grad1" /> {t.title}
              </h2>

              <p className="text-lg md:text-xl font-medium mt-4 text-custom-letters text-center border-b border-custom-grad1/20 pb-2">
                {t.locationHeader}
              </p>

              <FieldRow label={t.city}>
                <select
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                  className="w-full bg-custom-background px-4 py-2 rounded-full text-sm">
                  <option value="">{t.selectCity}</option>
                  {cities.map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
                </select>
              </FieldRow>

              <FieldRow label={t.area}>
                <select
                  name="area"
                  value={form.area}
                  onChange={handleChange}
                  className="w-full bg-custom-background px-4 py-2 rounded-full text-sm"
                  disabled={!form.city}>
                  <option value="">
                    {form.city ? t.selectArea: t.selectCityFirst}
                  </option>
                  {areas.map((area) => (
                    <option key={area} value={area}>
                      {area}
                    </option>
                  ))}
                </select>
              </FieldRow>

              <FieldRow label={t.store}>
                <select
                  name="store"
                  value={form.store}
                  onChange={(e) => {
                    if (e.target.value === "ADD_NEW_STORE") {
                      navigate("/addstore");
                    } else {
                      handleChange(e);
                    }
                  }}
                  className="w-full bg-custom-background px-4 py-2 rounded-full text-sm outline-none border-none focus:ring-2 focus:ring-custom-letters"
                >
                  <option value="">{t.selectLocation}</option>
                  {locations.map((store) => (
                    <option key={store.store_id} value={store.store_id}>
                      {store.store_name} ({[store.area, store.road].filter(Boolean).join(" - ")})
                    </option>
                  ))}
                  {/* Διαχωριστικό και επιλογή για προσθήκη */}
                  <option disabled>──────────</option>
                  <option value="ADD_NEW_STORE" className="font-bold text-custom-grad1">
                    ➕ {t.addNewStore}
                  </option>
                </select>
              </FieldRow>

              <p className="text-lg md:text-xl font-medium mt-8 text-custom-letters text-center border-b border-custom-grad1/20 pb-2">
                {t.detailsHeader}
              </p>

              <FieldRow label={t.product}>
                <input
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  className="w-full bg-custom-background px-4 py-2 rounded-full"
                />
              </FieldRow>

              <FieldRow label={t.category}>
                <select
                  name="category"
                  value={form.category}
                  onChange={(e) => {
                    if (e.target.value === "ADD_NEW_CAT") {
                      setShowCategoryPopup(true);
                    } else {
                      handleChange(e);
                    }
                  }}
                  className="w-full bg-custom-background px-4 py-2 rounded-full text-sm outline-none border-none focus:ring-2 focus:ring-custom-letters"
                >
                  <option value="">{t.selectCategory}</option>
                  {categories.map((cat) => (
                    <option key={cat.category_id} value={cat.category_id}>
                      {language === "el" ? cat.category_name_el : cat.category_name}
                    </option>
                  ))}
                  <option disabled>──────────</option>
                  <option value="ADD_NEW_CAT" className="font-bold text-custom-grad1">
                    ➕ {t.addNewCat}
                  </option>
                </select>
              </FieldRow>
              <FieldRow label={t.originalPrice}>
                <input
                  name="originalPrice"
                  type="number"
                  value={form.originalPrice}
                  onChange={handleChange}
                  className="w-full bg-custom-background px-4 py-2 rounded-full"
                />
              </FieldRow>

              <FieldRow label={t.discountPrice}>
                <input
                  name="discountPrice"
                  type="number"
                  value={form.discountPrice}
                  onChange={handleChange}
                  className="w-full bg-custom-background px-4 py-2 rounded-full"
                />
              </FieldRow>

              <FieldRow label={t.discount}>
                <input
                  name="discount"
                  type="number"
                  value={form.discount}
                  readOnly
                  onChange={handleChange}
                  className="w-full bg-custom-background px-4 py-2 rounded-full opacity-70"
                />
              </FieldRow>

              {/* 
              {/* Add store button for redirect
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={() => navigate("/addstore")}
                  className="mt-3 inline-flex items-center gap-2 w-fit bg-gradient-to-r from-custom-grad1 via-custom-grad2 to-custom-grad4 text-white font-semibold px-4 py-2 rounded-full hover:opacity-90 transition"
                >
                  <Plus size={18} /> Add store
                </button>
              </div> */}

              {/* Photo */}
              <div className="flex gap-4 justify-center">
                <label className="cursor-pointer flex items-center gap-2 bg-custom-grad1 text-white px-4 py-2 rounded-full">
                  <Upload size={18} /> {t.upload}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => handlePhoto(e.target.files[0])}
                  />
                </label>

                <button
                  type="button"
                  onClick={() => {
                    cameraInputRef.current.value = null;
                    cameraInputRef.current.click();
                  }}
                  className="flex items-center gap-2 bg-custom-grad4 text-white px-4 py-2 rounded-full"
                >
                  <Camera size={18} />
                  {t.camera}
                </button>

                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  hidden
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      handlePhoto(e.target.files[0]);
                    }
                  }}
                />
              </div>

              {/*if preview exists show it*/}
              {photoPreview && (
                <img
                  src={photoPreview}
                  className="mx-auto w-48 h-48 object-cover rounded-xl shadow"
                />
              )}

              <div className="flex justify-center mt-6 w-full">
                <button
                  onClick={handleSubmit}
                  disabled={!isFormValid}
                  className={`mt-8 font-bold px-6 py-3 rounded-full shadow transition ${isFormValid
                    ? "bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white hover:opacity-90"
                    : "bg-gray-400 cursor-not-allowed opacity-60"
                    }`}>
                  {t.publish}
                </button>

                {showSuccessPopup && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div role="dialog" aria-modal="true" aria-labelledby="adddiscount-success-title" className="bg-custom-header rounded-2xl p-6 w-80 shadow-xl animate-fadeIn text-center">
                      <h3 id="adddiscount-success-title" className="text-lg font-semibold text-custom-letters mb-3">
                        {t.successTitle}
                      </h3>
                      <p className="text-sm text-custom-letters mb-6">
                        {t.successMsg}
                      </p>
                      <button
                        onClick={() => window.location.reload()}
                        className="px-6 py-2 rounded-full bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-custom-letters hover:opacity-90 transition">
                        OK
                      </button>
                    </div>
                  </div>
                )}

                {offlineQueuedPopup && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div role="dialog" aria-modal="true" className="bg-custom-header rounded-2xl p-6 w-80 shadow-xl animate-fadeIn text-center">
                      <h3 className="text-lg font-semibold text-custom-letters mb-3">
                        {t.offlineTitle}
                      </h3>
                      <p className="text-sm text-custom-letters mb-6">
                        {t.offlineMsg}
                      </p>
                      <button
                        onClick={() => { setOfflineQueuedPopup(false); window.location.reload(); }}
                        className="px-6 py-2 rounded-full bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-custom-letters hover:opacity-90 transition">
                        OK
                      </button>
                    </div>
                  </div>
                )}

                {showCategoryPopup && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div role="dialog" aria-modal="true" aria-labelledby="adddiscount-cat-title" className="bg-custom-header rounded-2xl p-6 w-full max-w-sm shadow-xl text-center">
                      <h3 id="adddiscount-cat-title" className="text-lg font-semibold text-custom-letters mb-4">
                        {t.addNewCat}
                      </h3>

                      <input
                        autoFocus
                        className="w-full bg-custom-background px-4 py-3 rounded-full text-sm border-none focus:ring-2 focus:ring-custom-grad1"
                        placeholder={language === "el" ? "π.χ. Γαλακτοκομικά" : "e.g. Dairy"}
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                      />

                      <div className="flex justify-center gap-3 mt-6">
                        <button
                          onClick={() => setShowCategoryPopup(false)}
                          className="px-4 py-2 text-sm text-custom-letters hover:text-red-500 transition"
                        >
                          {t.cancel}
                        </button>
                        <button
                          onClick={handleSaveCategory}
                          disabled={!newCategoryName}
                          className={`px-8 py-2 rounded-full font-bold text-white transition ${newCategoryName ? "bg-gradient-to-r from-custom-grad1 to-custom-grad4" : "bg-gray-300"
                            }`}
                        >
                          {t.save}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>

      {errorPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]">
          <div role="dialog" aria-modal="true" aria-labelledby="adddiscount-error-title" className="bg-custom-header rounded-2xl p-6 w-80 shadow-xl text-center">
            <h3 id="adddiscount-error-title" className="text-lg font-semibold text-custom-letters mb-3">{errorPopup.title}</h3>
            <p className="text-sm text-custom-letters mb-6">{errorPopup.msg}</p>
            <button
              onClick={() => setErrorPopup(null)}
              className="px-6 py-2 rounded-full bg-gradient-to-r from-custom-grad1 to-custom-grad4 text-custom-letters font-bold">
              {t.ok}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddDiscount;
